import { useQuery } from '@tanstack/react-query';
import { ShieldAlert, ShieldCheck, ShieldQuestion } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';

interface CategoryReading {
  key: string;
  label: string;
  score: number;
  threshold: number;
  tripped: boolean;
}

interface SightengineResponse {
  ok: true;
  mediaKind: 'image' | 'video';
  mediaUrl: string;
  trippedAny: boolean;
  summary: string;
  categories: CategoryReading[];
  frameCount?: number;
  firstTrippedFrameSeconds?: number | null;
  source?: 'stored' | 'live';
  computedAt: string;
}

interface SightengineAnalysisProps {
  postId: string;
}

export function SightengineAnalysis({
  postId,
}: SightengineAnalysisProps): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['moderation', 'sightengine', postId],
    queryFn: () =>
      apiFetch<SightengineResponse>(
        `/api/moderation/posts/${postId}/sightengine`,
      ),
    // No polling — analysis is stable for the open lifetime of the lightbox.
    refetchInterval: false,
    staleTime: 5 * 60_000,
  });

  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-bold uppercase tracking-wide text-fg-muted">
        Sightengine analysis
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <ShieldQuestion size={14} />
          Loading analysis…
        </div>
      )}

      {error && <ErrorRow error={error as Error} />}

      {data && (
        <>
          <div className="flex items-start gap-2">
            {data.trippedAny ? (
              <ShieldAlert size={16} className="text-state-danger mt-0.5 shrink-0" />
            ) : (
              <ShieldCheck size={16} className="text-brand-cyan mt-0.5 shrink-0" />
            )}
            <div className="text-sm">
              <span
                className={cn(
                  'font-medium',
                  data.trippedAny ? 'text-state-danger' : 'text-brand-cyan',
                )}
              >
                {data.trippedAny ? 'Flagged' : 'Clean'}
              </span>{' '}
              <span className="text-fg-muted">{data.summary}</span>
              {data.mediaKind === 'video' && data.frameCount !== undefined && (
                <span className="text-fg-subtle">
                  {' '}
                  · {data.frameCount} frames analysed
                  {data.firstTrippedFrameSeconds != null &&
                    ` · first tripped at ${data.firstTrippedFrameSeconds.toFixed(1)}s`}
                </span>
              )}
            </div>
          </div>

          <ul className="flex flex-col gap-1 pt-1">
            {data.categories.map((c) => (
              <CategoryRow key={c.key} c={c} />
            ))}
          </ul>

          <div className="text-xs text-fg-subtle pt-1">
            {data.source === 'stored' ? (
              <>
                From upload check ·{' '}
                {new Date(data.computedAt).toLocaleString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </>
            ) : (
              <>
                Live re-analysis ·{' '}
                {new Date(data.computedAt).toLocaleTimeString()} · cached 5 min
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ErrorRow({ error }: { error: Error }): JSX.Element {
  const apiErr = error instanceof ApiError ? error : null;
  // 503 = credentials not configured. 400 = no media URL. 502 = upstream
  // Sightengine failure (e.g., media URL 404'd). Be specific.
  let body = error.message;
  if (apiErr?.status === 503) {
    body = 'Sightengine credentials not set on the admin server.';
  } else if (apiErr?.status === 502) {
    body = `Sightengine couldn't analyse the media — ${error.message}`;
  }
  return (
    <div className="flex items-start gap-2 rounded-md border border-state-warning/30 bg-state-warning/5 px-3 py-2 text-sm">
      <ShieldQuestion size={14} className="text-state-warning mt-0.5 shrink-0" />
      <span className="text-fg-muted">{body}</span>
    </div>
  );
}

function CategoryRow({ c }: { c: CategoryReading }): JSX.Element {
  // Position the score on a 0..1 axis, with a vertical marker at the threshold.
  const pct = Math.min(100, Math.max(0, c.score * 100));
  const thresholdPct = c.threshold * 100;
  return (
    <li className="flex items-center gap-3 text-sm">
      <span
        className={cn(
          'w-36 shrink-0',
          c.tripped ? 'text-fg font-medium' : 'text-fg-muted',
        )}
      >
        {c.label}
      </span>
      <div className="relative flex-1 h-2 rounded-full bg-surface-bg overflow-hidden">
        <div
          className={cn(
            'absolute inset-y-0 left-0 transition-all',
            c.tripped ? 'bg-state-danger' : 'bg-fg-subtle/60',
          )}
          style={{ width: `${pct}%` }}
        />
        {/* Threshold marker — a thin vertical line at the trip point */}
        <div
          className="absolute inset-y-0 w-px bg-fg/80"
          style={{ left: `${thresholdPct}%` }}
          title={`Threshold ${c.threshold.toFixed(2)}`}
        />
      </div>
      <span
        className={cn(
          'w-12 text-right tabular-nums text-xs',
          c.tripped ? 'text-state-danger font-bold' : 'text-fg-subtle',
        )}
      >
        {c.score.toFixed(3)}
      </span>
    </li>
  );
}
